import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * PhotoUpload — uploads an image to /api/uploads/photo and returns the URL.
 * Value is the relative URL (e.g. /api/files/amalvbc/photos/.../uuid.png).
 */
export default function PhotoUpload({ value, onChange, testid, label = "Photo" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5 Mo)");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error("Échec du téléversement");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange("");

  const displayUrl = value ? toAuthUrl(value) : null;

  return (
    <div data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">{label}</div>
      <div className="flex items-center gap-3">
        <div className="h-20 w-20 rounded-md border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {displayUrl ? (
            <img src={displayUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-7 w-7 text-slate-300" />
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Button type="button" size="sm" variant="outline"
            onClick={handlePick} disabled={uploading}
            data-testid={`${testid}-button`}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
            {uploading ? "Envoi…" : (value ? "Remplacer" : "Téléverser")}
          </Button>
          {value && (
            <Button type="button" size="sm" variant="ghost"
              onClick={clear} className="text-red-600 h-7 text-xs"
              data-testid={`${testid}-clear`}>
              <X className="h-3 w-3 mr-1" /> Retirer
            </Button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFile}
          className="hidden"
          data-testid={`${testid}-input`}
        />
      </div>
      <div className="text-[10px] text-slate-400 mt-1">JPG, PNG ou WebP — max 5 Mo</div>
    </div>
  );
}

// Convert a relative /api/files/... URL into a full URL with token query param
// so <img src> works without Authorization header.
export function toAuthUrl(relUrl) {
  if (!relUrl) return null;
  if (relUrl.startsWith("http")) return relUrl;
  const token = localStorage.getItem("amal_token");
  const sep = relUrl.includes("?") ? "&" : "?";
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  if (relUrl.startsWith("/api/files/") && token) {
    return `${backend}${relUrl}${sep}token=${encodeURIComponent(token)}`;
  }
  return `${backend}${relUrl}`;
}
